// The "+ Add material" / "+ Add service" control at the foot of each section.
//
// Collapsed it is a dashed button; opened it is a search box over the platform catalogue with a
// quantity beside each result. Both collections are small and are platform reference data, so
// they load once through the generic masters endpoint and filter in the browser — a bespoke
// search endpoint per collection would be sprawl for a list this size.
//
// The added line comes back priced by the catalogue: its packaging conversion, the supplier's MOQ
// and order multiple, and the tenant's price in force — exactly as a generated line. It is then
// editable, swappable and re-suppliable like any other, which a hand-typed custom line is not.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAddCatalogueMaterialLineMutation,
  useAddCatalogueServiceLineMutation,
  useListCatalogueServicesQuery,
  useListCatalogueVariantsQuery,
} from '@/redux/internal-services/estimatesApi';

const money = (n?: number | null, currency = 'USD') =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);

interface Props {
  kind: 'material' | 'service';
  versionCode: string;
  currency: string;
  commit: (run: () => Promise<unknown>) => Promise<void>;
}

export default function AddLinePicker({ kind, versionCode, currency, commit }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [quantity, setQuantity] = useState('1');

  // Nothing loads until the picker is opened: the catalogue is irrelevant to a reader who never
  // adds a line, and every open estimate would otherwise pull rows it may never show.
  const { data: variants, isLoading: loadingItems } = useListCatalogueVariantsQuery(undefined, {
    skip: !open || kind !== 'material',
  });
  const { data: services, isLoading: loadingBoq } = useListCatalogueServicesQuery(undefined, {
    skip: !open || kind !== 'service',
  });
  const [addMaterial] = useAddCatalogueMaterialLineMutation();
  const [addService] = useAddCatalogueServiceLineMutation();

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (kind === 'material') {
      // Every product_variant_master row IS a purchasable material — labour and equipment are
      // services now, so the picker no longer has to filter them out by item type.
      const pool = variants ?? [];
      const matched = q
        ? pool.filter((v) => `${v.variant_name} ${v.product_code} ${v.product_variant_code}`
          .toLowerCase().includes(q))
        : pool;
      return matched.slice(0, 40).map((v) => ({
        code: v.product_variant_code,
        title: v.variant_name,
        meta: [v.purchase_uom, v.units_per_package ? `${v.units_per_package} / ${v.purchase_uom}` : null]
          .filter(Boolean).join(' · '),
        // Price depends on the supplier and the estimate's date, so the picker does not claim one
        // — the added line comes back priced.
        price: null as number | null,
      }));
    }
    const pool = services ?? [];
    const matched = q
      ? pool.filter((sv) => `${sv.service_name} ${sv.service_category ?? ''} ${sv.service_code}`
        .toLowerCase().includes(q))
      : pool;
    return matched.slice(0, 40).map((sv) => ({
      code: sv.service_code,
      title: sv.service_name,
      meta: [sv.service_type_code, sv.default_uom].filter(Boolean).join(' · '),
      price: null as number | null,
    }));
  }, [variants, services, search, kind]);

  const add = (code: string) => commit(async () => {
    const qty = Number(quantity) || 1;
    if (kind === 'material') {
      await addMaterial({ estimate_version_code: versionCode, product_variant_code: code, quantity: qty }).unwrap();
    } else {
      await addService({ estimate_version_code: versionCode, service_code: code, quantity: qty }).unwrap();
    }
    setOpen(false);
    setSearch('');
    setQuantity('1');
  });

  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        className="mt-0.5 w-full rounded-lg border border-dashed border-ui-border bg-ui-card px-3 py-2 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
      >
        {kind === 'material'
          ? `+ ${t('estimates.editor.addMaterial', 'Add material')}`
          : `+ ${t('estimates.editor.addService', 'Add service')}`}
      </button>
    );
  }

  const loading = kind === 'material' ? loadingItems : loadingBoq;

  return (
    <div className="mt-0.5 rounded-lg border border-dashed border-ui-border bg-ui-card p-2">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={kind === 'material'
            ? t('estimates.editor.searchMaterials', 'Search materials…')
            : t('estimates.editor.searchServices', 'Search work & services…')}
          className="min-w-0 flex-1 rounded-md border border-ui-border bg-ui-card px-2 py-1 text-xs text-ui-text focus:border-ui-ring focus:outline-none"
        />
        <span className="text-xs text-ui-text3">{t('estimates.editor.qty', 'Qty')}</span>
        <input
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          inputMode="decimal"
          className="w-14 rounded-md border border-ui-border bg-ui-card px-1.5 py-1 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
        />
        <button
          type="button" onClick={() => setOpen(false)}
          className="text-xs font-medium text-ui-text3 hover:underline"
        >
          {t('workflow.cancel', 'Cancel')}
        </button>
      </div>

      <div className="mt-1.5 max-h-56 overflow-y-auto">
        {loading && (
          <p className="px-1 py-2 text-xs text-ui-text3">{t('estimates.editor.loading', 'Loading…')}</p>
        )}
        {!loading && results.length === 0 && (
          <p className="px-1 py-2 text-xs text-ui-text3">
            {t('estimates.editor.noCatalogueMatches', 'Nothing in the catalogue matches that.')}
          </p>
        )}
        {results.map((r) => (
          <button
            key={r.code} type="button" onClick={() => void add(r.code)}
            className="flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1 text-left hover:bg-ui-hover"
          >
            <span className="min-w-0">
              <span className="block truncate text-xs font-medium text-ui-text">{r.title}</span>
              {r.meta && <span className="block text-xs text-ui-text3">{r.meta}</span>}
            </span>
            <span className="flex-shrink-0 text-xs text-ui-text2">{money(r.price, currency)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
